import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, useTheme } from '@mui/material';
import useDate from '../../hooks/useDate/useDate';
import useLogger from '../../hooks/useLogger/useLogger';
import useSnackBar from '../../hooks/useSnackBar/useSnackBar';

import { Sort } from '../../models/CommonModels';
import { DevicesReport, DevicesReportFilter, DevicesReportRequest } from '../../models/DevicesReportModels';
import { FileFormat } from '../../models/ImportModels';

import DevicesReportService from '../../services/DevicesReports/DevicesReportService';

import { Header, Row } from '../../components/Table/TableModels';
import ClickableLink from '../../components/Text/ClickableLink';
import DevicesReportRowOptions from './components/DevicesReportRowOptions';
import TruncatedTooltip from '../../components/TruncatedTooltip/TruncatedTooltip';

const defaultHeaders = [
  'endUserFirstName',
  'endUserLastName',
  'endUserEmail',
  'manufacturer',
  'model',
  'type',
  'os',
  'mobileNumber',
  'eid',
  'imei',
  'serialNumber',
  'source',
  'uemId',
  'uemSystem',
  'status',
  'companyName',
  'country',
  'addedDate',
  'lastUpdatedDate',
  'assetTagNumber',
];

const unassignedHeaders = [
  'manufacturer',
  'model',
  'type',
  'eid',
  'imeiOne',
  'imeiTwo',
  'serialNumber',
  'deviceSource',
  'uemProvider',
  'creationDate',
  'updateDate',
  'assetTagNumber',
  'freeText',
];

function useDevicesReportTable(
  activePage: number,
  pageSize: number,
  filter: DevicesReportFilter,
  sort: Sort,
  unassignedReport: boolean,
) {
  const [t] = useTranslation();
  const { localeDateTimeFormat } = useDate();
  const [totalItems, setTotalItems] = useState(0);
  const [devicesReport, setDevicesReport] = useState<DevicesReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const logger = useLogger();
  const snackBar = useSnackBar();
  const theme = useTheme();

  const [tableHeaders, setTableHeaders] = useState(!unassignedReport ? defaultHeaders : unassignedHeaders);
  const [openRowId, setOpenRowId] = useState<string | null>(null);

  /**
   * Fetch the list of devices report
   */
  const fetchDevicesReport = () => {
    setIsLoading(true);
    const requestParams: DevicesReportRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order,
      ...filter,
    };
    if (!requestParams.status) {
      delete requestParams.status;
    }
    DevicesReportService.api.fetchDevicesReport(requestParams).then(
      (response) => {
        setDevicesReport(response.data.deviceReportList);
        setTotalItems(response.data.totalElements);
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.devicesReport.fetchDevicesReportError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Fetch the list of unassigned devices report
   */
  const fetchUnassignedDevicesReport = () => {
    setIsLoading(true);
    const requestParams: DevicesReportRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order,
      ...filter,
    };
    if (!requestParams.status) {
      delete requestParams.status;
    }
    DevicesReportService.api.fetchUnassignedDevicesReport(requestParams).then(
      (response) => {
        setDevicesReport(response.data.devices);
        setTotalItems(response.data.totalElements);
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.devicesReport.fetchDevicesReportError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Exports the current devices report
   */
  const exportData = (format: FileFormat) => {
    setIsLoading(true);
    const requestParams: DevicesReportRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order.toUpperCase(),
      ...filter,
    };
    if (!filter.status) {
      delete requestParams.status;
    }

    DevicesReportService.api
      .exportData(requestParams, format, `devices_report_${DateTime.now().toFormat(localeDateTimeFormat)}.${format}`)
      .catch(() => {
        snackBar.showSnackBar(t('pages.devicesReport.exportFileError'), 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  React.useEffect(() => {
    if (unassignedReport) {
      DevicesReportService.api.fetchUnassignedTableColumns().then(
        (response) => {
          setTableHeaders(response.data.columns);
        },
        () => {
          logger.logError('DevicesReport: Failed to get the list of columns');
        },
      );
    } else {
      DevicesReportService.api.fetchTableColumns().then(
        (response) => {
          setTableHeaders(response.data.columns);
        },
        () => {
          logger.logError('DevicesReport: Failed to get the list of columns');
        },
      );
    }
  }, [unassignedReport]);

  React.useEffect(() => {
    if (unassignedReport) fetchUnassignedDevicesReport();
    else fetchDevicesReport();
  }, [activePage, pageSize, filter, sort, unassignedReport]);

  /**
   * Render the value of each cell based on type of field
   * @param dr
   * @param item
   */
  const renderCellValue = (dr: DevicesReport, item: keyof DevicesReport) => {
    if (dr[item]) {
      switch (item) {
        case 'serialNumber':
        case 'eid':
        case 'imei':
        case 'imeiOne':
        case 'imeiTwo':
          return <TruncatedTooltip label={dr[item]} limit={4} />;
        case 'lastUpdatedDate':
        case 'addedDate':
        case 'creationDate':
        case 'updateDate':
          return DateTime.fromISO(dr[item]).toFormat(`${localeDateTimeFormat} (z)`);
        case 'status':
          return t(`common.simStatus.${dr[item]}`);
        case 'type':
          return t(`common.deviceType.${dr[item]}`);
        case 'uemSystem':
          return t(`pages.uemOnboarding.uemTypes.${dr[item]}`);
        case 'freeText': {
          return dr[item].length > 250 ? `${dr[item].slice(0, 250)}…` : dr[item];
        }
        default:
          return dr[item];
      }
    }
    return t('common.na');
  };

  /**
   * Calc cell min width
   */
  const calcWidth = (dr: DevicesReport, item: keyof DevicesReport) => {
    if (dr[item]) {
      switch (item) {
        case 'freeText': {
          const len = Math.min(dr[item].length, 250);
          const width = 12 + (len / 250) * (35 - 12);
          return `${width}rem`;
        }
        default:
          return '12rem';
      }
    }

    return '12rem';
  };

  const headers: Header[] = useMemo(() => {
    const hds: Header[] = tableHeaders.map((item) => ({
      id: item,
      label: t(`pages.devicesReport.table.headers.${item}`),
      isSortable: true,
      sx: { minWidth: '12rem' },
    }));
    hds.push({
      id: 'viewDetails',
      label: '',
      sx: {
        position: 'sticky',
        right: 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 1,
          boxShadow: '-8px 0px 16px 0px #00000029',
          opacity: 'var(--sticky-actions-shadow, 0)',
          pointerEvents: 'none',
          transition: 'opacity 120ms ease',
        },
      },
    });
    return hds;
  }, [t, tableHeaders]);

  const rows: Row[] = useMemo(
    () =>
      devicesReport?.map((dr, index) => {
        // True when the row ID is the same to the row with active drawer
        const isMenuOpen = openRowId === dr.id;

        return {
          id: dr.itemId,
          cells: [
            ...tableHeaders.map((item) => ({
              value: renderCellValue(dr, item as keyof DevicesReport),
              sx: {
                minWidth: calcWidth(dr, item as keyof DevicesReport),
                ...(item === 'freeText' ? { whiteSpace: 'wrap' } : { whiteSpace: 'nowrap' }),
              },
            })),
            {
              value: !unassignedReport ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    padding: '0.688rem 1rem',
                    '& a': {
                      whiteSpace: { xs: 'normal', md: 'nowrap' },
                    },
                  }}
                >
                  <ClickableLink
                    to={`${
                      dr.endUserId
                        ? `/dashboard/devicesReport/userDetails/${dr.endUserId}/deviceDetails/${dr.id}`
                        : `/dashboard/devicesReport/deviceDetails/${dr.id}`
                    }`}
                  >
                    {t('common.table.viewDetails')}
                  </ClickableLink>
                </Box>
              ) : (
                <DevicesReportRowOptions
                  device={dr}
                  isPerformingAction={isLoading}
                  onAssignSuccess={fetchUnassignedDevicesReport}
                  // We track which row has the side drawer open and change the z-index so the other action columns don't overlap the menu
                  onToggleDrawer={() => setOpenRowId((prev) => (prev === dr.id ? null : dr.id))}
                />
              ),
              sx: {
                position: 'sticky',
                right: 0,
                p: 0,
                // z-index 1150 on the cell with active drawer
                zIndex: isMenuOpen ? 1150 : 1,
                backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: 1,
                  boxShadow: '-8px 0px 16px 0px #00000029',
                  opacity: 'var(--sticky-actions-shadow, 0)',
                  pointerEvents: 'none',
                  transition: 'opacity 120ms ease',
                },
              },
            },
          ],
        };
      }),
    [t, devicesReport, tableHeaders, openRowId],
  );

  return { headers, rows, totalItems, isLoading, exportData };
}

export default useDevicesReportTable;
